/**
 * NewUserWelcome.tsx
 *
 * Shown on the ExpiryItems page when the account has zero real documents.
 * Contains:
 *   1. Friendly hero greeting + 3-step onboarding flow
 *   2. Sample document preview table covering all major categories / status types
 *   3. "Send test email" card — lets users verify email notifications work
 *   4. Quick-action CTAs: Scan & Add / Import CSV
 *
 * Design: light, neutral palette so it doesn't clash with the blue BetaBanner
 * at the top of the page.
 */

import React, { useState, useEffect } from "react";
import api from "../config/api";
import { APP_NAME } from "../constants/expiry";

// ─── Props ────────────────────────────────────────────────────────────────────
interface Props {
  userName?: string;
  userEmail?: string;
  onAddDocument: () => void;
  onImport: () => void;
  /** Called when user wants to fully dismiss the welcome / sample-data screen */
  onDismiss: () => void;
}

// ─── Sample data ──────────────────────────────────────────────────────────────
// Hard-coded relative dates so the preview always looks fresh without DB queries.
function daysFromNow(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

type SampleStatus = "expired" | "high-risk" | "upcoming" | "safe";

interface SampleDoc {
  emoji: string;
  name: string;
  category: string;
  expiry: string;
  daysLeft: number;
  status: SampleStatus;
}

const SAMPLE_DOCS: SampleDoc[] = [
  {
    emoji: "📋",
    name: "Trade License",
    category: "Licenses & Permits",
    expiry: daysFromNow(5),
    daysLeft: 5,
    status: "high-risk",
  },
  {
    emoji: "📝",
    name: "Employee NDA",
    category: "HR",
    expiry: daysFromNow(-3),
    daysLeft: -3,
    status: "expired",
  },
  {
    emoji: "🛡️",
    name: "Professional Indemnity Insurance",
    category: "Insurance",
    expiry: daysFromNow(22),
    daysLeft: 22,
    status: "upcoming",
  },
  {
    emoji: "🍽️",
    name: "Food Safety Inspection",
    category: "Health & Safety",
    expiry: daysFromNow(14),
    daysLeft: 14,
    status: "upcoming",
  },
  {
    emoji: "🏆",
    name: "ISO 9001 Certification",
    category: "Certifications",
    expiry: daysFromNow(48),
    daysLeft: 48,
    status: "safe",
  },
  {
    emoji: "🏢",
    name: "Office Lease Agreement",
    category: "Real Estate",
    expiry: daysFromNow(90),
    daysLeft: 90,
    status: "safe",
  },
  {
    emoji: "💼",
    name: "Vendor Service Contract",
    category: "Vendor",
    expiry: daysFromNow(65),
    daysLeft: 65,
    status: "safe",
  },
];

// ─── Status badge helpers ─────────────────────────────────────────────────────
const STATUS_CONFIG: Record<
  SampleStatus,
  { label: string; bg: string; text: string }
> = {
  expired: { label: "Expired", bg: "#FEF2F2", text: "#DC2626" },
  "high-risk": { label: "High Risk", bg: "#FEF2F2", text: "#DC2626" },
  upcoming: { label: "Upcoming", bg: "#FFFBEB", text: "#D97706" },
  safe: { label: "Active", bg: "#F0FDF4", text: "#16A34A" },
};

function StatusBadge({ status }: { status: SampleStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      style={{ background: cfg.bg, color: cfg.text }}
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
    >
      {cfg.label}
    </span>
  );
}

function DaysChip({ days }: { days: number }) {
  if (days < 0)
    return (
      <span className="text-xs text-[#DC2626] font-medium">
        {Math.abs(days)}d overdue
      </span>
    );
  if (days <= 7)
    return (
      <span className="text-xs text-[#DC2626] font-medium">{days}d left</span>
    );
  if (days <= 30)
    return (
      <span className="text-xs text-[#D97706] font-medium">{days}d left</span>
    );
  return (
    <span className="text-xs text-[#16A34A] font-medium">{days}d left</span>
  );
}

// ─── How-it-works steps ───────────────────────────────────────────────────────
const STEPS = [
  {
    icon: "🤖",
    step: "01",
    title: "Scan & Add",
    desc: "Upload a document or image — AI extracts the name, category, and expiry date automatically.",
  },
  {
    icon: "🔔",
    step: "02",
    title: "Agent Sends Reminders",
    desc: "Sentrilog's AI agent monitors every document and emails you 30, 7, and 1 day before expiry.",
  },
  {
    icon: "✅",
    step: "03",
    title: "Renew & Stay Compliant",
    desc: "Click Renew right from your inbox to mark it done and reset the reminder cycle.",
  },
];

// ─── Component ────────────────────────────────────────────────────────────────
const NewUserWelcome: React.FC<Props> = ({
  userName,
  userEmail,
  onAddDocument,
  onImport,
  onDismiss,
}) => {
  const firstName = userName?.split(" ")[0] || "there";

  // ── Test-email state ────────────────────────────────────────────────────────
  const [testStatus, setTestStatus] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");
  const [testMsg, setTestMsg] = useState("");
  // Auto-hide the test-email card 3 s after a successful send
  const [testCardVisible, setTestCardVisible] = useState(true);

  useEffect(() => {
    if (testStatus === "sent") {
      const t = setTimeout(() => setTestCardVisible(false), 3000);
      return () => clearTimeout(t);
    }
  }, [testStatus]);

  const sendTestEmail = async () => {
    setTestStatus("sending");
    setTestMsg("");
    try {
      const res = await api.post("/expiry/send-test-reminder");
      setTestStatus("sent");
      setTestMsg(res.data.message || `Test email sent to ${userEmail}`);
    } catch (err: any) {
      setTestStatus("error");
      setTestMsg(
        err?.response?.data?.error || "Failed to send. Check your email config."
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* ── 1. Hero welcome card ─────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm overflow-hidden">
        {/* Thin accent bar */}
        <div className="h-1 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#2563EB]" />

        <div className="px-6 py-7 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
            {/* Left — greeting */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">👋</span>
                <h2 className="text-xl font-bold text-[#111827]">
                  Welcome to {APP_NAME}, {firstName}!
                </h2>
              </div>
              <p className="text-sm text-[#6B7280] mb-5 max-w-lg leading-relaxed">
                You're all set. Add your first document and the AI agent will
                start monitoring expiry dates and sending automated reminders.
              </p>

              {/* CTA buttons */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={onAddDocument}
                  className="inline-flex items-center gap-2 px-5 py-2.5
                             bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-semibold
                             rounded-xl transition-colors shadow-sm"
                >
                  <span className="text-base">🤖</span>
                  Scan &amp; Add Document
                </button>
                <button
                  onClick={onImport}
                  className="inline-flex items-center gap-2 px-5 py-2.5
                             bg-white hover:bg-[#F9FAFB] border border-[#E5E7EB]
                             text-[#374151] text-sm font-semibold rounded-xl transition-colors"
                >
                  <span className="text-base">📥</span>
                  Import via CSV
                </button>
              </div>
            </div>

            {/* Right — quick stat pill */}
            <div className="flex-shrink-0 self-start sm:self-center">
              <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-2xl px-5 py-4 text-center min-w-[130px]">
                <div className="text-3xl font-bold text-[#16A34A]">0</div>
                <div className="text-xs text-[#4B5563] mt-0.5 font-medium">
                  Documents tracked
                </div>
                <div className="text-[10px] text-[#9CA3AF] mt-1">
                  Add your first one ↓
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 2. How it works ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {STEPS.map((s) => (
          <div
            key={s.step}
            className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm px-5 py-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">{s.icon}</span>
              <span className="text-[10px] font-bold text-[#9CA3AF] tracking-widest uppercase">
                Step {s.step}
              </span>
            </div>
            <h3 className="text-sm font-bold text-[#111827] mb-1">{s.title}</h3>
            <p className="text-xs text-[#6B7280] leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>

      {/* ── 3. Test email card — hidden after successful send ────────────── */}
      {testCardVisible && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm overflow-hidden">
          <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
            {/* Icon */}
            <div className="flex-shrink-0 h-11 w-11 bg-[#EFF6FF] rounded-xl flex items-center justify-center">
              <svg
                className="h-5 w-5 text-[#2563EB]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-[#111827]">
                Verify email notifications
              </h3>
              <p className="text-xs text-[#6B7280] mt-0.5">
                Send a sample reminder to{" "}
                <span className="font-medium text-[#374151]">
                  {userEmail || "your email"}
                </span>{" "}
                to confirm delivery is working.
              </p>

              {/* Status message */}
              {testStatus === "sent" && (
                <p className="text-xs text-[#16A34A] mt-1.5 font-medium flex items-center gap-1">
                  <svg
                    className="h-3.5 w-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {testMsg}
                </p>
              )}
              {testStatus === "error" && (
                <p className="text-xs text-[#DC2626] mt-1.5 font-medium">
                  ⚠️ {testMsg}
                </p>
              )}
            </div>

            {/* Button */}
            <button
              onClick={sendTestEmail}
              disabled={testStatus === "sending" || testStatus === "sent"}
              className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5
                       border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB]
                       disabled:opacity-50 disabled:cursor-not-allowed
                       text-[#374151] text-sm font-medium rounded-xl transition-colors whitespace-nowrap"
            >
              {testStatus === "sending" ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-[#6B7280]"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Sending…
                </>
              ) : testStatus === "sent" ? (
                <>
                  <svg
                    className="h-4 w-4 text-[#16A34A]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Email sent ✓
                </>
              ) : (
                <>
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Send test email
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── 4. Sample data preview table ─────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm overflow-hidden">
        {/* Header bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#F3F4F6] bg-[#FAFAFA]">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[#374151]">
              Sample Documents Preview
            </span>
            <span className="inline-flex items-center px-2 py-0.5 bg-[#F3F4F6] border border-[#E5E7EB] text-[#6B7280] text-[10px] font-bold rounded-full tracking-wide uppercase">
              Demo only
            </span>
          </div>
          <p className="text-xs text-[#9CA3AF] hidden sm:block">
            Your real documents will appear here
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#F3F4F6]">
                <th className="text-left px-5 py-2.5 text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">
                  Document
                </th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide hidden sm:table-cell">
                  Category
                </th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">
                  Expiry Date
                </th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide hidden md:table-cell">
                  Days Left
                </th>
                <th className="text-right px-5 py-2.5 text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {SAMPLE_DOCS.map((doc, i) => (
                <tr
                  key={i}
                  className="border-b border-[#F9FAFB] last:border-0 opacity-60 hover:opacity-80 transition-opacity"
                >
                  {/* Document name */}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <span className="text-base flex-shrink-0">
                        {doc.emoji}
                      </span>
                      <span className="font-medium text-[#111827] truncate max-w-[180px]">
                        {doc.name}
                      </span>
                    </div>
                  </td>
                  {/* Category */}
                  <td className="px-4 py-3 text-[#6B7280] hidden sm:table-cell whitespace-nowrap">
                    {doc.category}
                  </td>
                  {/* Expiry date */}
                  <td className="px-4 py-3 text-[#374151] whitespace-nowrap">
                    {doc.expiry}
                  </td>
                  {/* Days left */}
                  <td className="px-4 py-3 hidden md:table-cell">
                    <DaysChip days={doc.daysLeft} />
                  </td>
                  {/* Status */}
                  <td className="px-5 py-3 text-right">
                    <StatusBadge status={doc.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Bottom CTA inside the table */}
        <div className="px-5 py-4 bg-[#FAFAFA] border-t border-[#F3F4F6] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-xs text-[#6B7280]">
            👆 These are sample documents. Add yours to start getting real
            reminders.
          </p>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            {/* Primary CTA */}
            <button
              onClick={onAddDocument}
              className="inline-flex items-center gap-1.5 px-4 py-2
                         bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-semibold
                         rounded-lg transition-colors whitespace-nowrap"
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add your first document
            </button>

            {/* Dismiss / delete sample data */}
            <button
              onClick={onDismiss}
              title="Remove sample data"
              className="inline-flex items-center gap-1.5 px-3.5 py-2
                         bg-white hover:bg-[#FEF2F2] border border-[#E5E7EB] hover:border-[#FECACA]
                         text-[#6B7280] hover:text-[#DC2626] text-xs font-medium
                         rounded-lg transition-colors whitespace-nowrap"
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Remove sample data
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewUserWelcome;
